import { defineStore } from 'pinia';
import { computed, ref, type Ref } from 'vue';
import handleRequest from '@/utils/helpers/handleRequest';
import Api from '@/utils/api/index.js';
import useTransform from 'lodash/transform.js';
import sortBy from 'lodash/sortBy.js';
import orderBy from 'lodash/orderBy.js';
export type TSelectRecord = Record<string | number, unknown>;

type TUserQuestionnaireSettings = {
  hide_remote: boolean;
  show_non_sustainable_preferred_transports: boolean;
  ask_off_site_work: boolean;
};

export const transformSelect = (options: TSelectRecord) =>
  useTransform(
    options,
    (acc, name, value) => {
      acc.push({ value, name });
    },
    [] as TSelectRecord[],
  );

const useGlobalStore = defineStore(
  'globalStore',
  () => {
    const loadingCount = ref(0);
    const pageKey = ref<string | number>(0);
    const countries = ref<({ name: string; value: number } | undefined)[]>([]);
    const citySubscriptions = ref<string[]>([]);
    const countriesLoading = ref(false);
    const dashboardAvailableYears = ref<number[]>([]);
    const dashboardLoading = ref(false);
    const companyAvailableYears = ref<{ name: number; value: number }[]>([]);
    const companyLoading = ref(false);
    const selectDashboardYear = ref<null | number>(null);
    const selectCompanyIdFromYear = ref(0);
    const userQuestionnaireSettings = ref<TUserQuestionnaireSettings>({
      hide_remote: false,
      show_non_sustainable_preferred_transports: false,
      ask_off_site_work: false,
    });
    const userQuestionnaireInitialCountryId = ref<number | null>(null);

    const currentYearCompanyId = computed<number>(() => {
      const currentYear = new Date().getFullYear();
      return (
        companyAvailableYears.value.find(({ name }) => name === currentYear)
          ?.value ||
        companyAvailableYears.value?.at(-1)?.value ||
        0
      );
    });

    // The year a company manager is looking at: the one chosen in the menu, else the current one.
    const selectedYearCompanyId = computed<number>(
      () => selectCompanyIdFromYear.value || currentYearCompanyId.value,
    );

    const isLoading = computed(() => loadingCount.value > 0);

    const toggleLoading = (loaded: boolean) => {
      if (loaded) {
        loadingCount.value++;
      } else {
        loadingCount.value = Math.max(0, loadingCount.value - 1);
      }
    };

    const fetchData = async (
      stateRef: Ref,
      loadingRef: Ref<boolean>,
      url: string,
      transform = (x: any) => x,
      force = false,
    ) => {
      if (loadingRef.value || (stateRef.value.length && !force)) return;
      loadingRef.value = true;
      const { isError, data } = await handleRequest(Api.get(url));
      if (!isError) stateRef.value = await transform(data);
      loadingRef.value = false;
    };

    const fetchCountries = (force = false) =>
      fetchData(
        countries,
        countriesLoading,
        '/location/countries',
        transformSelect,
        force,
      );

    const setDashboardYear = (year: string | number) => {
      selectDashboardYear.value = year ? +year : null;
    };

    const setUserQuestionnaireSettings = (
      settings: TUserQuestionnaireSettings,
    ) => {
      userQuestionnaireSettings.value = settings;
    };

    const setUserQuestionnaireInitialCountryId = (id: number | null) => {
      userQuestionnaireInitialCountryId.value = id;
    };

    const setSelectCompanyIdFromYear = (id: number) => {
      selectCompanyIdFromYear.value = id;
    };

    const fetchDashboardAvailableYears = async (force = false) => {
      await fetchData(
        dashboardAvailableYears,
        dashboardLoading,
        '/dashboard/available-years',
        d => {
          const selected = selectDashboardYear.value;
          if (!selected || !d.some(item => item === selected)) {
            setDashboardYear(d?.at(-1) || null);
          }

          return sortBy(d);
        },
        force,
      );
    };

    const fetchCompanyAvailableYears = (force = false) =>
      fetchData(
        companyAvailableYears,
        companyLoading,
        '/available-years',
        async d => {
          const res = orderBy(transformSelect(d), ['name']);
          const selected = selectCompanyIdFromYear.value;
          if (!selected || !res.some(item => item.value === selected)) {
            setSelectCompanyIdFromYear((res.at(-1)?.value as number) || 0);
          }

          return res;
        },
        force,
      );

    const setCitySubscriptions = (data: string[] = []) => {
      citySubscriptions.value = data;
    };

    const updatePageKey = (key?: number | string) => {
      pageKey.value = key ? key : +pageKey.value + 1;
    };

    const reset = () => {
      loadingCount.value = 0;
      citySubscriptions.value = [];
      dashboardAvailableYears.value = [];
      companyAvailableYears.value = [];
      companyLoading.value = false;
      dashboardLoading.value = false;
      // selectDashboardYear.value = null;
      // selectCompanyIdFromYear.value = 0;
    };

    return {
      isLoading,
      loadingCount,
      countries,
      citySubscriptions,
      dashboardLoading,
      companyLoading,
      countriesLoading,
      dashboardAvailableYears,
      companyAvailableYears,
      selectDashboardYear,
      selectCompanyIdFromYear,
      pageKey,
      currentYearCompanyId,
      selectedYearCompanyId,
      userQuestionnaireInitialCountryId,
      userQuestionnaireSettings,
      setUserQuestionnaireSettings,
      setUserQuestionnaireInitialCountryId,
      updatePageKey,
      setSelectCompanyIdFromYear,
      setDashboardYear,
      fetchCountries,
      fetchDashboardAvailableYears,
      fetchCompanyAvailableYears,
      transformSelect,
      setCitySubscriptions,
      toggleLoading,
      reset,
    };
  },
  {
    persist: {
      storage: localStorage,
      pick: ['selectCompanyIdFromYear', 'selectDashboardYear'],
    },
  },
);

export default useGlobalStore;
